import type { Suggestion, SuggestState } from './formulaSuggest'

// FANUC(FOCAS) 태그 주소 — "함수[,인자…][@필드.하위필드]" (LiveEdge FocasTagConverter).
// 함수·인자 개수·필드는 LiveEdge FocasPacketReader 와 실제 빌드되는 FwLib 구조체(조건부 기호 없음) 기준이고,
// 뜻은 Edge팀 "Live Edge CNC(Fanuc) 설정 매뉴얼 v1.1" 기준. 매뉴얼 예시의 끝 [1] 은 화면의 크기 표기라 주소에 넣지 않는다.
// 필드를 비우면 LiveEdge 가 함수별 기본 필드를 읽는다 (defaultField).

type Option = { value: string; label: string }
type Param = { name: string; label: string; options?: (prev: string[]) => Option[] }
type Field = { name: string; label: string; children?: Field[] }

interface FocasFunction {
  name: string
  label: string
  params: Param[]
  fields?: Field[]
  defaultField?: string
}

const range = (from: number, to: number, label: (n: number) => string): Option[] =>
  Array.from({ length: to - from + 1 }, (_, i) => ({ value: String(from + i), label: label(from + i) }))

const loadElm: Field[] = [
  { name: 'data', label: '값' }, { name: 'dec', label: '소수점 자리' }, { name: 'unit', label: '단위' }, { name: 'name', label: '이름' },
]

const AXIS_TYPES: Record<string, Option[]> = {
  '1': [
    { value: '0', label: '절대 위치' }, { value: '1', label: '기계 좌표' }, { value: '2', label: '상대 위치' }, { value: '3', label: '남은 이동 거리' },
    { value: '4', label: '핸들 인터럽트 입력값' }, { value: '5', label: '핸들 인터럽트 출력값' }, { value: '6', label: '프로그램 재시작 시작 위치' },
    { value: '7', label: '프로그램 재시작 남은 거리' }, { value: '8', label: '블록 재시작 시작 위치' }, { value: '9', label: '블록 재시작 남은 거리' },
  ],
  '2': [{ value: '0', label: 'servo 부하율' }, { value: '1', label: '부하 전류(%)' }, { value: '2', label: '부하 전류(전류값)' }],
  '3': [
    { value: '0', label: 'spindle 부하율' }, { value: '1', label: 'spindle 모터 속도' }, { value: '2', label: 'spindle 속도(3799#2 기준)' },
    { value: '3', label: 'spindle 속도(모터 속도 기반)' }, { value: '4', label: 'spindle 부하율(250ms 평균)' }, { value: '5', label: 'spindle 최대 부하율' },
    { value: '6', label: 'spindle 최소 부하율(250ms 평균)' }, { value: '7', label: 'spindle 지속 동작 가능 시간' },
  ],
  '5': [
    { value: '0', label: 'feedRate (축 이동 속도)' }, { value: '1', label: 'spindle 속도 (RPM)' }, { value: '2', label: 'jog / dry run 속도' },
    { value: '3', label: 'tool tip 속도' }, { value: '4', label: 'servo 모터 회전 속도' }, { value: '5', label: 'feedRate (F/S)' },
  ],
}
AXIS_TYPES['4'] = AXIS_TYPES['3']

export const FOCAS_FUNCTIONS: FocasFunction[] = [
  {
    name: 'cnc_rdparam', label: '파라미터 (카운트·시간 등)', defaultField: 'idata',
    params: [
      { name: 'A', label: '파라미터 번호', options: () => [
        { value: '6711', label: '부품 카운트' }, { value: '6754', label: 'cutting time' }, { value: '6758', label: 'cycle time' },
        { value: '6752', label: 'operation time' }, { value: '6750', label: 'power on time' },
      ] },
      { name: 'B', label: '축', options: () => [{ value: '0', label: '축 지정 없음' }, { value: '-1', label: '모든 축' }, ...range(1, 8, n => `${n}번 축`)] },
      { name: 'C', label: '데이터 길이', options: () => [{ value: '8', label: '축 지정 없을 때 8' }] },
    ],
    fields: [{ name: 'cdata', label: 'bit/byte 파라미터' }, { name: 'idata', label: 'word 파라미터' }, { name: 'ldata', label: '2 word 파라미터' }],
  },
  {
    // 시작(ms)~마지막(분) 파라미터 쌍을 한 번에 읽어 h:mm:ss 로 돌려준다. LiveEdge 는 시작 6751~6758 만 읽는다
    name: 'cnc_rdparar', label: '파라미터 범위 → 시간 (h:mm:ss)',
    params: [
      { name: 'A', label: '시작 파라미터', options: () => [
        { value: '6751', label: 'operating time' }, { value: '6753', label: 'cutting time' }, { value: '6757', label: 'cycle time' },
      ] },
      { name: 'B', label: '축', options: () => [{ value: '0', label: '축 지정 없음' }] },
      { name: 'C', label: '마지막 파라미터', options: prev => {
        const start = Number(prev[0])
        return Number.isFinite(start) && prev[0] ? [{ value: String(start + 1), label: `${start + 1} (시작 다음 번호)` }] : []
      } },
      { name: 'D', label: '크기', options: () => [{ value: '8', label: '8' }] },
    ],
  },
  {
    name: 'cnc_rdaxisdata', label: '축·spindle 데이터, feedRate', defaultField: 'data1.data',
    params: [
      { name: 'A', label: 'cls 데이터 클래스', options: () => [
        { value: '1', label: '위치값' }, { value: '2', label: 'servo' }, { value: '3', label: 'spindle' }, { value: '4', label: '특정 spindle' }, { value: '5', label: '속도' },
      ] },
      { name: 'B', label: 'type', options: prev => AXIS_TYPES[prev[0]] ?? [] },
      { name: 'C', label: 'type 배열 개수', options: () => [{ value: '1', label: 'type 1개' }] },
      { name: 'D', label: '읽을 축 개수', options: () => range(1, 8, n => `${n}개`) },
    ],
    fields: range(1, 8, n => `${n}번 축`).map(o => ({ name: `data${o.value}`, label: o.label, children: loadElm })),
  },
  {
    name: 'cnc_rdspmeter', label: 'spindle 부하율·모터 속도', defaultField: 'spload',
    params: [
      { name: 'A', label: 'type', options: () => [{ value: '-1', label: '부하율·모터 속도 둘 다' }, { value: '0', label: 'spindle 부하율' }, { value: '1', label: 'spindle 모터 속도' }] },
      { name: 'B', label: '데이터 개수', options: () => [{ value: '1', label: '1개' }] },
    ],
    fields: [{ name: 'spload', label: 'spindle 부하율', children: loadElm }, { name: 'spspeed', label: 'spindle 모터 속도', children: loadElm }],
  },
  {
    name: 'cnc_rdtimer', label: '동작 시간 (h:m:s)',
    params: [{ name: 'A', label: 'type', options: () => [
      { value: '0', label: 'power on time' }, { value: '1', label: 'operating time' }, { value: '2', label: 'cutting time' },
      { value: '3', label: 'cycle time' }, { value: '4', label: 'free purpose' },
    ] }],
  },
  {
    name: 'cnc_rdgrpinfo', label: 'tool 수명 (사용 횟수)', defaultField: 'data1.count_value',
    params: [
      { name: 'A', label: '시작 tool 번호', options: () => range(1, 5, n => `tool ${n}`) },
      { name: 'B', label: '끝 tool 번호', options: () => range(1, 5, n => `tool ${n}`) },
      { name: 'C', label: '데이터 길이', options: () => [{ value: '24', label: '24' }] },
    ],
    fields: range(1, 5, n => `${n}번 tool`).map(o => ({
      name: `data${o.value}`, label: o.label, children: [
        { name: 'n_tool', label: 'tool 번호' }, { name: 'count_value', label: 'tool 수명 값' },
        { name: 'counter', label: 'tool 수명 카운터' }, { name: 'count_type', label: '카운터 타입' },
      ],
    })),
  },
  {
    name: 'cnc_statinfo', label: 'CNC 상태', defaultField: 'aut', params: [],
    fields: [
      { name: 'aut', label: '자동 모드 선택' }, { name: 'run', label: '자동 동작 상태' }, { name: 'motion', label: '축 이동 상태' },
      { name: 'mstb', label: 'M·S·T·B 상태' }, { name: 'emergency', label: '비상 상태' }, { name: 'alarm', label: '알람 상태' },
      { name: 'edit', label: '편집 상태' }, { name: 'tmmode', label: 'T/M 모드' },
    ],
  },
  {
    name: 'cnc_exeprgname', label: '실행 중 프로그램', defaultField: 'name', params: [],
    fields: [{ name: 'name', label: '프로그램 이름' }, { name: 'o_num', label: '프로그램 번호' }],
  },
  {
    name: 'cnc_rdmacro', label: '매크로 변수', defaultField: 'mcr_val',
    params: [{ name: 'A', label: '매크로 변수 번호', options: () => [{ value: '500', label: '#500 (예)' }] }],
    fields: [{ name: 'mcr_val', label: '값' }, { name: 'dec_val', label: '소수점 자리' }],
  },
  {
    name: 'cnc_rdsvmeter', label: 'servo 부하율', defaultField: 'svload1.data', params: [],
    fields: range(1, 8, n => `${n}번 축`).map(o => ({ name: `svload${o.value}`, label: o.label, children: loadElm })),
  },
  {
    name: 'cnc_rdtofs', label: '공구 보정값', defaultField: 'data',
    params: [{ name: 'A', label: '공구 번호' }, { name: 'B', label: 'type' }, { name: 'C', label: '데이터 길이', options: () => [{ value: '8', label: '8' }] }],
    fields: [{ name: 'data', label: '보정값' }],
  },
  {
    name: 'cnc_rdopnlsgnl', label: '조작반 신호 (오버라이드 등)',
    params: [{ name: 'A', label: 'slct (읽을 신호 비트)' }],
    fields: [
      { name: 'mode', label: '모드' }, { name: 'feed_ovrd', label: '이송 오버라이드' }, { name: 'spdl_ovrd', label: 'spindle 오버라이드' },
      { name: 'rpd_ovrd', label: '급속 오버라이드' }, { name: 'jog_ovrd', label: 'jog 오버라이드' }, { name: 'sngl_blck', label: '싱글 블록' },
      { name: 'blck_del', label: '블록 삭제' }, { name: 'machn_lock', label: '머신 록' }, { name: 'dry_run', label: 'dry run' },
      { name: 'mem_prtct', label: '메모리 보호' }, { name: 'feed_hold', label: 'feed hold' }, { name: 'hndl_ax', label: '핸들 축' }, { name: 'hndl_mv', label: '핸들 이동량' },
    ],
  },
  {
    name: 'cnc_rdprogdir3', label: '프로그램 목록', defaultField: 'dir1.number',
    params: [{ name: 'A', label: 'type' }, { name: 'B', label: '시작 프로그램 번호' }],
    fields: [{ name: 'dir1', label: '첫 프로그램', children: [
      { name: 'number', label: '번호' }, { name: 'length', label: '길이' }, { name: 'page', label: '페이지' }, { name: 'comment', label: '설명' },
    ] }],
  },
  { name: 'cnc_rdzofs', label: '워크 좌표 보정', params: [{ name: 'A', label: '보정 번호' }, { name: 'B', label: '축' }, { name: 'C', label: '데이터 길이' }] },
  {
    name: 'pmc_rdpmcrng', label: 'PMC 신호',
    params: [
      { name: 'A', label: '주소 종류', options: () => ['G', 'F', 'Y', 'X', 'A', 'R', 'T', 'K', 'C', 'D'].map((t, i) => ({ value: String(i), label: t })) },
      { name: 'B', label: '데이터 형', options: () => [
        { value: '0', label: 'byte' }, { value: '1', label: 'word' }, { value: '2', label: 'long' }, { value: '4', label: 'float' }, { value: '5', label: 'double' },
      ] },
      { name: 'C', label: '주소 번호' },
    ],
  },
]

const BY_NAME = new Map(FOCAS_FUNCTIONS.map(f => [f.name, f]))

// 자주 쓰는 주소 — 한글로 찾는다 (매뉴얼 예시 기준)
export const FOCAS_PRESETS: { label: string; address: string; keywords: string }[] = [
  { label: '부품 카운트', address: 'cnc_rdparam,6711,0,8@ldata', keywords: '카운트 수량 생산 count part' },
  { label: 'cutting time (파라미터)', address: 'cnc_rdparam,6754,0,8@ldata', keywords: '가공 시간 절삭 cutting' },
  { label: 'cycle time (파라미터)', address: 'cnc_rdparam,6758,0,8@ldata', keywords: '사이클 ct cycle' },
  { label: 'operation time (파라미터)', address: 'cnc_rdparam,6752,0,8@ldata', keywords: '운전 가동 operation' },
  { label: 'power on time (파라미터)', address: 'cnc_rdparam,6750,0,8@ldata', keywords: '전원 power' },
  { label: 'cycle time (h:m:s)', address: 'cnc_rdtimer,3', keywords: '사이클 ct cycle 타이머' },
  { label: 'cutting time (h:m:s)', address: 'cnc_rdtimer,2', keywords: '가공 절삭 cutting 타이머' },
  { label: 'operating time (h:m:s)', address: 'cnc_rdtimer,1', keywords: '운전 가동 operating 타이머' },
  { label: 'power on time (h:m:s)', address: 'cnc_rdtimer,0', keywords: '전원 power 타이머' },
  { label: 'cycle time (파라미터 범위)', address: 'cnc_rdparar,6757,0,6758,8', keywords: '사이클 ct cycle 범위' },
  { label: 'cutting time (파라미터 범위)', address: 'cnc_rdparar,6753,0,6754,8', keywords: '가공 시간 절삭 cutting 범위' },
  { label: 'operating time (파라미터 범위)', address: 'cnc_rdparar,6751,0,6752,8', keywords: '운전 가동 operating 범위' },
  { label: 'spindle 부하율', address: 'cnc_rdspmeter,-1,1@spload.data', keywords: '스핀들 주축 부하 load' },
  { label: 'spindle 모터 속도', address: 'cnc_rdspmeter,-1,1@spspeed.data', keywords: '스핀들 주축 모터 속도 speed' },
  { label: 'feedRate (이송 속도)', address: 'cnc_rdaxisdata,5,0,1,1@data1.data', keywords: '이송 피드 속도 feed' },
  { label: 'spindle 속도 (RPM)', address: 'cnc_rdaxisdata,5,1,1,1@data1.data', keywords: '스핀들 주축 회전 rpm 속도' },
  { label: '1번 축 절대 위치', address: 'cnc_rdaxisdata,1,0,1,1@data1.data', keywords: '축 위치 좌표 절대 x position' },
  { label: '1번 축 servo 부하율', address: 'cnc_rdaxisdata,2,0,1,1@data1.data', keywords: '서보 부하 servo load' },
  { label: '자동 운전 상태', address: 'cnc_statinfo@run', keywords: '상태 운전 가동 run' },
  { label: '알람 상태', address: 'cnc_statinfo@alarm', keywords: '알람 alarm 이상' },
  { label: '비상 정지 상태', address: 'cnc_statinfo@emergency', keywords: '비상 emergency' },
  { label: '자동 모드', address: 'cnc_statinfo@aut', keywords: '모드 mode 자동' },
  { label: '실행 프로그램 번호', address: 'cnc_exeprgname@o_num', keywords: '프로그램 번호 program' },
  { label: '실행 프로그램 이름', address: 'cnc_exeprgname@name', keywords: '프로그램 이름 program' },
  { label: 'tool 수명 값 (1번)', address: 'cnc_rdgrpinfo,1,1,24@data1.count_value', keywords: '공구 툴 수명 tool' },
  { label: '매크로 변수 #500', address: 'cnc_rdmacro,500@mcr_val', keywords: '매크로 변수 macro' },
]

const item = (label: string, insert: string, hint: string, preview?: string): Suggestion => ({ label, insert, hint, preview })

/** 주소 칸 자동완성 (수식이 아닐 때). title 은 목록 머리에 보일 안내 */
export function focasSuggest(value: string, caret: number): (SuggestState & { title?: string }) | null {
  const before = value.slice(0, caret)

  // 함수 고르기 — 단어(한글 포함)로 자주 쓰는 주소, 함수 이름
  if (!/[,@]/.test(before)) {
    const q = before.trim().toLowerCase()
    const presets = FOCAS_PRESETS
      .filter(p => !q || p.label.toLowerCase().includes(q) || p.keywords.includes(q) || p.address.startsWith(q))
      .map(p => item(p.address, p.address, p.label))
    const funcs = FOCAS_FUNCTIONS
      .filter(f => !q || f.name.includes(q) || f.label.toLowerCase().includes(q))
      .map(f => item(f.name, f.name + (f.params.length ? ',' : f.fields ? '@' : ''), f.label, `인자 ${f.params.length}`))
    const items = [...presets, ...funcs].filter(i => i.insert !== before.trim())
    return items.length ? { items, from: 0, to: value.length, title: q ? undefined : '자주 쓰는 주소 · FOCAS 함수' } : null
  }

  const fn = BY_NAME.get(before.split(/[,@]/)[0].trim())
  if (!fn) return null

  // 필드 — @ 뒤, 점으로 하위 필드
  const at = before.indexOf('@')
  if (at >= 0) {
    const segs = before.slice(at + 1).split('.')
    const current = segs.pop() ?? ''
    let level = fn.fields ?? []
    for (const s of segs) level = level.find(f => f.name === s)?.children ?? []
    const items = level
      .filter(f => f.name.toLowerCase().startsWith(current.toLowerCase()) && !(f.name === current && !f.children))
      .map(f => item(f.name, f.name + (f.children ? '.' : ''), f.label))
    if (!items.length) return null
    const title = segs.length ? `${fn.name} @${segs.join('.')}.` : `${fn.name} 필드${fn.defaultField ? ` (비우면 ${fn.defaultField})` : ''}`
    return { items, from: caret - current.length, to: caret, title }
  }

  // 인자 — 몇 번째인지 보고 뜻·선택지
  const parts = before.slice(before.indexOf(',') + 1).split(',')
  const index = parts.length - 1
  const current = parts[index].trim()
  const param = fn.params[index]
  if (!param) {
    // 인자를 다 썼다 — 필드로 넘어가기
    return fn.fields ? { items: [item('@', '@', '필드 고르기')], from: caret, to: caret, title: `${fn.name} 인자 끝` } : null
  }
  const last = index === fn.params.length - 1
  const next = last ? (fn.fields ? '@' : '') : ','
  const options = (param.options?.(parts.slice(0, index).map(p => p.trim())) ?? [])
    .filter(o => !current || o.value.startsWith(current))
    .filter(o => !(o.value === current && !next))
    .map(o => item(o.value, o.value + next, o.label))
  const title = `${fn.name} — ${param.name}: ${param.label} (${index + 1}/${fn.params.length})`
  if (!options.length) {
    // 선택지 없는 자유 입력 — 안내만 (Enter 를 가로채지 않도록 목록은 비운다)
    return null
  }
  return { items: options, from: caret - parts[index].length, to: caret, title }
}

/** 전송 전 점검 — 함수 이름·인자 개수 */
export function focasCheck(address: string): string | null {
  const a = address.trim()
  const fn = BY_NAME.get(a.split(/[,@]/)[0])
  if (!fn) return a.startsWith('cnc_rdexecprog') || a.startsWith('cnc_rdspload') ? null : '함수 이름 확인'
  const params = a.includes(',') ? a.slice(a.indexOf(',') + 1).split('@')[0].split(',') : []
  if (params.length !== fn.params.length) return `인자 ${fn.params.length}개 필요`
  if (params.some(p => !p.trim())) return '빈 인자'
  return null
}
